import { useId } from "react";
import type { PropertyPublic } from "@townsy/contracts";
import { useBootstrapQuery, useMyPermissionsQuery } from "../../api/queries";
import { commercialCheckout } from "../../commerce/checkoutTargets";
import { commercialOffer, type CommercialOffer } from "../../commerce/gates";
import { propertyDisplayName } from "../../lib/propertyLabels";
import { RESERVED_NOTICE_MS, useTownStore } from "../../state/store";
import { useDollarGate } from "./useDollarGate";

/**
 * "Buy for $X" / "Take over for $X" on a commercial building (docs/01 section 6, docs/04 section 3):
 * explains who receives how much Townsy Credit, the buyer's new basis, and that an owner cannot buy
 * their own building. The click opens the global checkout; the server quotes the real price.
 */
export function useCommercialOffer(item: PropertyPublic, opts: { checkPermissions: boolean }): { offer: CommercialOffer; notice: string | null; reservedRecently: boolean } {
  const boot = useBootstrapQuery();
  const signedIn = Boolean(boot.data?.session.userId);
  const gate = useDollarGate({ commercial: true });
  const perms = useMyPermissionsQuery(opts.checkPermissions && item.type === "COMMERCIAL" ? item.id : null, signedIn);
  const reservedAt = useTownStore((s) => s.reservedSeen[item.id] ?? 0);
  const reservedRecently = reservedAt > 0 && Date.now() - reservedAt < RESERVED_NOTICE_MS;
  const offer = commercialOffer({
    property: item,
    gate,
    viewerActorId: signedIn ? (boot.data?.actor.actorId ?? null) : null,
    isOwner: perms.data?.isOwner ?? null,
    reservedByOther: reservedRecently,
    paymentsMode: gate.config?.mode ?? null,
  });
  return { offer, notice: gate.config?.notice ?? null, reservedRecently };
}

/** Presentational (server-rendered in unit tests). */
export function CommercialOfferView({
  offer,
  notice,
  reservedRecently,
  onBuy,
}: {
  offer: CommercialOffer;
  notice: string | null;
  reservedRecently: boolean;
  onBuy(): void;
}) {
  const reasonId = useId();
  return (
    <div className="stack commerce-offer" style={{ gap: 6 }} data-testid="commercial-offer" data-mode={offer.mode}>
      {offer.isOwner ? (
        <div className="notice notice-info small" data-testid="self-takeover-note">
          <strong>This is your building.</strong> {offer.reason}
        </div>
      ) : (
        <>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!offer.enabled}
            aria-describedby={offer.reason ? reasonId : undefined}
            onClick={onBuy}
            data-testid="commercial-buy"
          >
            {offer.label}
          </button>
          {offer.reason ? (
            <span id={reasonId} className="hint" data-testid="commercial-buy-reason">
              {offer.reason}
            </span>
          ) : null}
        </>
      )}
      {reservedRecently && !offer.isOwner ? (
        <div className="notice notice-warning small" role="status" data-testid="commercial-reserved">
          Another member is checking out this building. Their hold lasts at most 10 minutes; nothing is charged to you.
        </div>
      ) : null}
      <ul className="small commerce-lines" style={{ margin: 0, paddingLeft: 18 }} data-testid="commercial-lines">
        {offer.lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {notice ? (
        <p className="hint" style={{ margin: 0 }}>
          {notice}
        </p>
      ) : null}
    </div>
  );
}

/** Property panel section. */
export function CommercialOfferSection({ item }: { item: PropertyPublic }) {
  const { offer, notice, reservedRecently } = useCommercialOffer(item, { checkPermissions: true });
  const openCheckout = useTownStore((s) => s.openCheckout);
  return (
    <CommercialOfferView
      offer={offer}
      notice={notice}
      reservedRecently={reservedRecently}
      onBuy={() => openCheckout(commercialCheckout(item, propertyDisplayName(item)))}
    />
  );
}

/** Compact button for directory rows (same rules; ownership from the public owner id only). */
export function CommercialBuyButton({ item, onOpened }: { item: PropertyPublic; onOpened?: () => void }) {
  const { offer } = useCommercialOffer(item, { checkPermissions: false });
  const openCheckout = useTownStore((s) => s.openCheckout);
  const reason = offer.isOwner ? "Your building: you cannot buy your own building." : offer.reason;
  return (
    <button
      type="button"
      className="btn btn-sm"
      disabled={!offer.enabled}
      title={reason ?? undefined}
      aria-label={reason ? `${offer.label}. ${reason}` : offer.label}
      onClick={() => {
        onOpened?.();
        openCheckout(commercialCheckout(item, propertyDisplayName(item)));
      }}
      data-testid={`directory-buy-${item.id}`}
    >
      {offer.isOwner ? "Your building" : offer.label}
    </button>
  );
}
